'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { cn } from '@achra/ui/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@achra/ui/table';
import { ArrowRight, ArrowUpDown } from 'lucide-react';
import Link from 'next/link';
import SimpleBar from 'simplebar-react';
import type { BuilderProfileState } from '@/modules/__generated__/graphql/switchboard-generated';
import { BuilderProfile } from '@/modules/shared/components/builder-profile';
import { BuilderSkills } from '@/modules/shared/components/builder-skills';
import { BuildersSkeleton } from '../../builders-skeleton';
import { LastModified } from '../last-modified';
import { Links } from '../links';
import { NoBuildersFound } from '../no-builders-found/no-builders-found';
import { SortEnum, useBuildersTable } from './use-builders-table';
import type { Route } from 'next';
export interface BuildersTableProps {
  builders: BuilderProfileState[];
  className?: string;
  networkSlug: string;
  asSectionContent?: boolean;
}
function BuildersTable(t0) {
  const $ = _c(20);
  const {
    builders,
    className,
    networkSlug,
    asSectionContent: t1
  } = t0;
  const asSectionContent = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== asSectionContent || $[1] !== builders) {
    t2 = {
      builders,
      asSectionContent
    };
    $[0] = asSectionContent;
    $[1] = builders;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    headersSort,
    sortedBuilders,
    proccesedBuildersTableColumns,
    isResetPending,
    simpleBarRef,
    itemsWrapperRef,
    cardContentRef,
    handleSortClick
  } = useBuildersTable(t2);
  let t3;
  if ($[3] !== className) {
    t3 = cn("w-full", className);
    $[3] = className;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== asSectionContent || $[6] !== builders || $[7] !== cardContentRef || $[8] !== handleSortClick || $[9] !== headersSort || $[10] !== isResetPending || $[11] !== itemsWrapperRef || $[12] !== networkSlug || $[13] !== proccesedBuildersTableColumns || $[14] !== simpleBarRef || $[15] !== sortedBuilders) {
    t4 = isResetPending ? <BuildersSkeleton /> : builders.length === 0 ? <NoBuildersFound /> : <div className="w-full"><Table variant="pills" className={cn("[&_table]:block [&_table]:w-full", asSectionContent && "px-2")}><TableHeader className="mb-2 inline-block w-full"><TableRow className="flex h-fit w-full border-b-0! py-4">{proccesedBuildersTableColumns.map((column, index) => <TableHead key={column.accessorKey} className={cn("inline-block h-fit p-0!", column.isNumeric && "text-right", index === 0 && "w-[35%] pl-4! xl:pl-6!", index === 1 && "w-[30%]", index === 2 && "w-[24%]", index === 3 && "flex-1")}><Button variant="ghost" aria-label={`Sort ${column.header} column`} onClick={() => {
                handleSortClick(index);
              }} className={cn("[&_path]:stroke-foreground/30 hover:[&_path]:stroke-foreground/50 active:[&_path]:stroke-foreground! h-fit p-0! font-semibold hover:bg-transparent lg:text-base/6 dark:hover:bg-transparent", headersSort[index] === SortEnum.Asc && "[&_path:nth-child(3)]:stroke-foreground [&_path:nth-child(4)]:stroke-foreground hover:[&_path:nth-child(3)]:stroke-foreground/50 hover:[&_path:nth-child(4)]:stroke-foreground/50 hover:[&_path:nth-child(1)]:stroke-foreground/30 hover:[&_path:nth-child(2)]:stroke-foreground/30", headersSort[index] === SortEnum.Desc && "[&_path:nth-child(1)]:stroke-foreground [&_path:nth-child(2)]:stroke-foreground hover:[&_path:nth-child(1)]:stroke-foreground/50 hover:[&_path:nth-child(2)]:stroke-foreground/50 hover:[&_path:nth-child(3)]:stroke-foreground/30 hover:[&_path:nth-child(4)]:stroke-foreground/30")}>{column.header}<ArrowUpDown className="size-4" /></Button></TableHead>)}<TableHead className="h-fit flex-1 p-0!" /><TableHead className="h-fit p-0!" /></TableRow></TableHeader></Table><div ref={cardContentRef}><SimpleBar ref={simpleBarRef} className={cn(!asSectionContent && "-mx-2 px-2 pb-3")} autoHide={false}><div ref={itemsWrapperRef} className={cn(asSectionContent && "px-2 pb-2")}><Table variant="pills" className="[&_table]:block [&_table]:w-full"><TableBody className="flex flex-col gap-2">{sortedBuilders.map(builder => {
                  const builderLink = `/network/${networkSlug}/builders/${builder.slug ?? ""}` as Route;
                  return <TableRow key={builder.id} className="flex h-fit w-full cursor-pointer items-center border-b-0!" aria-label={`View ${builder.name} builder`}><TableCell className="inline-flex h-20.5 min-w-[35%] p-0!"><Link href={builderLink} className="w-full py-4! pr-0! pl-4! xl:pl-6!"><BuilderProfile name={builder.name} code={builder.code} status={builder.status} image={builder.icon} isOperator={builder.isOperator} /></Link></TableCell><TableCell className="inline-flex h-20.5 min-w-[30%] items-center p-0!"><Link href={builderLink} className="w-full px-0! py-4!"><BuilderSkills skills={builder.skills} className="h-fit" /></Link></TableCell><TableCell className="inline-flex h-20.5 flex-1 items-center p-0!"><Link href={builderLink} className="w-full px-0! py-4!"><LastModified lastModified={builder.lastModified} /></Link></TableCell><TableCell className="inline-flex h-20.5 w-fit items-center justify-end p-0! text-right"><Link href={builderLink} className="py-4! pr-4! pl-0!"><Links links={builder.links} /></Link></TableCell><TableCell className="inline-flex h-20.5 w-fit items-center p-0! text-right"><Link href={builderLink} className="py-4! pr-4! pl-0! xl:pr-6!"><Button variant="outline" size="icon" aria-label="View builder team details"><ArrowRight className="size-4" /></Button></Link></TableCell></TableRow>;
                })}</TableBody></Table></div></SimpleBar></div></div>;
    $[5] = asSectionContent;
    $[6] = builders;
    $[7] = cardContentRef;
    $[8] = handleSortClick;
    $[9] = headersSort;
    $[10] = isResetPending;
    $[11] = itemsWrapperRef;
    $[12] = networkSlug;
    $[13] = proccesedBuildersTableColumns;
    $[14] = simpleBarRef;
    $[15] = sortedBuilders;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t3 || $[18] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
export { BuildersTable };